<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import Icon from 'next/icon/Icon.vue';
import EventSidebarSection from './EventSidebarSection.vue';
import DropdownSearch from 'dashboard/components-next/dropdown-search/DropdownSearch.vue';
import { useSuppliersStore } from 'dashboard/stores/suppliers';

const props = defineProps({
  eventId: { type: String, default: null },
  supplierIds: { type: Array, default: () => [] },
  suppliers: { type: Array, default: () => [] },
  canManage: { type: Boolean, default: false },
});

const emit = defineEmits(['updated']);

const store = useStore();
const { t } = useI18n();
const suppliersStore = useSuppliersStore();
const showDropdown = ref(false);
const saving = ref(false);

const availableSuppliers = computed(() => suppliersStore.getSuppliersList || []);

const assignedSuppliers = computed(() => {
  const fromEvent = props.suppliers || [];
  if (fromEvent.length) return fromEvent;
  return availableSuppliers.value.filter(supplier =>
    props.supplierIds.includes(supplier.id)
  );
});

const selectedSuppliers = computed(() =>
  availableSuppliers.value.filter(supplier =>
    props.supplierIds.includes(supplier.id)
  )
);

const hasSuppliers = computed(() => assignedSuppliers.value.length > 0);

onMounted(async () => {
  if (props.canManage && !availableSuppliers.value.length) {
    await suppliersStore.get({ page: 1, sort: 'name' });
  }
});

const openEditor = () => {
  if (!props.canManage) return;
  showDropdown.value = true;
};

const toggleSupplier = async option => {
  if (saving.value) return;

  const ids = [...props.supplierIds];
  const index = ids.indexOf(option.id);
  if (index >= 0) ids.splice(index, 1);
  else ids.push(option.id);

  saving.value = true;
  try {
    const updatedEvent = await store.dispatch(
      'planningEvents/updateSuppliers',
      {
        id: props.eventId,
        supplierIds: ids,
      }
    );
    emit('updated', updatedEvent);
  } catch {
    useAlert(t('PLANNING.SHOW.SUPPLIERS.ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <EventSidebarSection
    :title="t('PLANNING.SHOW.SUPPLIERS.TITLE')"
    icon="i-lucide-truck"
  >
    <div class="flex flex-col gap-4">
      <div v-if="hasSuppliers" class="group/suppliers flex flex-col gap-3">
        <DropdownSearch
          v-if="showDropdown"
          :options="availableSuppliers"
          :selected-items="selectedSuppliers"
          multiple
          :has-thumbnail="false"
          :title="t('PLANNING.SHOW.SUPPLIERS.PLACEHOLDER')"
          :no-search-result="t('PLANNING.SHOW.SUPPLIERS.EMPTY')"
          :input-placeholder="t('PLANNING.SHOW.SUPPLIERS.PLACEHOLDER')"
          @select="toggleSupplier"
          @close="showDropdown = false"
        />
        <div
          v-for="(supplier, index) in assignedSuppliers"
          :key="supplier.id"
          class="relative flex items-center gap-3"
        >
          <button
            v-if="canManage && index === 0"
            class="absolute left-1.5 top-1/2 z-10 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-woot-500 hover:bg-woot-300 text-white group-hover/suppliers:inline-flex print:hidden"
            :title="t('PLANNING.SHOW.CHANGE_CONTACT')"
            @click.stop="openEditor"
          >
            <Icon icon="i-lucide-pen-line" class="size-4 shrink-0" />
          </button>
          <div
            class="flex size-10 shrink-0 items-center justify-center rounded-full bg-n-alpha-2"
          >
            <Icon icon="i-lucide-truck" class="size-4 text-n-slate-10" />
          </div>
          <p
            class="m-0 truncate text-sm font-semibold leading-5 text-n-slate-12"
          >
            {{ supplier.name }}
          </p>
        </div>
      </div>
      <div
        v-else-if="canManage"
        class="relative flex h-10 cursor-pointer items-center gap-3 rounded-lg hover:bg-n-alpha-2 print:hidden"
        @click.stop="openEditor"
      >
        <DropdownSearch
          v-if="showDropdown"
          :options="availableSuppliers"
          :selected-items="selectedSuppliers"
          multiple
          :has-thumbnail="false"
          :title="t('PLANNING.SHOW.SUPPLIERS.PLACEHOLDER')"
          :no-search-result="t('PLANNING.SHOW.SUPPLIERS.EMPTY')"
          :input-placeholder="t('PLANNING.SHOW.SUPPLIERS.PLACEHOLDER')"
          @select="toggleSupplier"
          @close="showDropdown = false"
        />
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-full bg-n-alpha-2"
        >
          <Icon icon="i-lucide-plus" class="size-4 text-n-slate-10" />
        </div>
        <p class="m-0 text-sm leading-5 text-black">
          {{ t('PLANNING.SHOW.SUPPLIERS.ASSIGN') }}
        </p>
      </div>
      <p v-else class="m-0 text-sm leading-5 text-black">
        {{ t('PLANNING.SHOW.SUPPLIERS.EMPTY') }}
      </p>
    </div>
  </EventSidebarSection>
</template>
